/**
 * @file src/pages/device-settings/components/OTPInputModal.jsx
 * @description Modal asking the user for the 6-digit OTP issued by the
 * ZATCA Fatoora Portal, used during compliance CSR generation.
 *
 * Pure form component. The parent (ComplianceCertificatesTab) owns the
 * actual generation logic — this modal just collects the OTP and calls
 * onSubmit(otp).
 */

import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Button from "components/ui/Button";
import Input from "components/ui/Input";
import Icon from "components/AppIcon";
import ModalContainer from "components/ui/ModalContainer";

const OTPInputModal = ({
  isOpen,
  onClose,
  onSubmit,
  isLoading,
  deviceInfo,
}) => {
  const { t } = useTranslation("pages");
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm();

  const otpValue = watch("otp");

  // Reset the form whenever the modal closes so reopening starts fresh
  useEffect(() => {
    if (!isOpen) reset();
  }, [isOpen, reset]);

  const onSubmitForm = (data) => {
    onSubmit(data.otp);
  };

  return (
    <ModalContainer isOpen={isOpen} onClose={onClose} maxWidth="max-w-md">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-foreground">
            {t("deviceModals.otp.title")}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground"
            disabled={isLoading}
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Device info banner */}
        <div className="mb-6">
          <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <Icon name="Info" size={16} className="text-blue-600" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">
                {t("deviceModals.otp.device", {
                  serial: deviceInfo?.deviceSerialNumber,
                })}
              </p>
              <p>{deviceInfo?.organizationName}</p>
            </div>
          </div>

          <p className="text-sm text-muted-foreground mb-4">
            {t("deviceModals.otp.instructions")}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmitForm)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              {t("deviceModals.otp.fields.otp")}
            </label>
            <Input
              {...register("otp", {
                required: t("deviceModals.otp.errors.required"),
                pattern: {
                  value: /^\d{6}$/,
                  message: t("deviceModals.otp.errors.digits"),
                },
              })}
              placeholder={t("deviceModals.otp.placeholders.otp")}
              maxLength={6}
              className="text-center text-lg tracking-widest"
              error={errors.otp?.message}
              autoFocus
            />
            <p className="text-xs text-muted-foreground mt-1">
              {t("deviceModals.otp.help")}
            </p>
          </div>

          <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
            <Button
              type="button"
              onClick={onClose}
              variant="outline"
              disabled={isLoading}
            >
              {t("common:actions.cancel")}
            </Button>
            <Button
              type="submit"
              disabled={isLoading || !otpValue || otpValue.length !== 6}
              className="bg-green-600 hover:bg-green-700 text-white"
            >
              {isLoading ? (
                <>
                  <Icon name="Loader" size={16} className="animate-spin" />
                  <span className="ms-2">
                    {t("deviceModals.otp.actions.generating")}
                  </span>
                </>
              ) : (
                <>
                  <Icon name="Shield" size={16} />
                  <span className="ms-2">
                    {t("deviceModals.otp.actions.generate")}
                  </span>
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </ModalContainer>
  );
};

export default OTPInputModal;
